import type { MessageDescriptor } from "@lingui/core"
import { msg } from "@lingui/core/macro"
import { SETTINGS_CARD_ANCHORS, type TSettingsCardAnchor, type TSettingsGroup } from "./groups"

/**
 * Hand-maintained search index for the settings page. One entry per concept card:
 * its group, scroll anchor, human title, and a lowercase-searchable set of setting
 * labels + descriptions. Deliberately static and typed — no runtime DOM scraping,
 * so search stays fast and predictable and a new setting only appears once it's
 * added here on purpose.
 *
 * `title` reuses the exact English string the card renders (same msgid, so no new
 * translation). `keywords` are locale-agnostic search aids — provider/product
 * names and a few English synonyms — matched alongside the translated title +
 * description so both English and Dutch queries land.
 */
export interface ISettingsIndexEntry {
  group: TSettingsGroup
  anchor: TSettingsCardAnchor
  title: MessageDescriptor
  description: MessageDescriptor
  /** Extra plain-text search aids (provider names, synonyms). Not translated. */
  keywords: readonly string[]
}

export const SETTINGS_INDEX: readonly ISettingsIndexEntry[] = [
  {
    group: "advanced",
    anchor: SETTINGS_CARD_ANCHORS.transcription,
    title: msg`Transcription services`,
    description: msg`These services turn speech into text, and the first one is tried first.`,
    keywords: [
      "soniox",
      "deepgram",
      "assemblyai",
      "speechmatics",
      "azure",
      "mai",
      "mai-transcribe",
      "azure-llm",
      "llm-speech",
      "azure llm",
      "api key",
      "byok",
      "provider order",
      "fallback",
      "failover",
      "priority",
      "primary",
      "streaming",
      "batch",
      "health",
      "circuit breaker",
    ],
  },
  {
    group: "general",
    anchor: SETTINGS_CARD_ANCHORS.language,
    title: msg`Languages and cleanup`,
    description: msg`Meeting languages, filler-word cleanup, and company words.`,
    keywords: [
      "languages",
      "dutch",
      "english",
      "german",
      "auto-detect",
      "transcript cleanup",
      "filler words",
      "company dictionary",
      "company words",
      "custom vocabulary",
      "jargon",
      "terms",
      "usage",
      "ranking",
      "heard",
      "hits",
    ],
  },
  {
    group: "general",
    anchor: SETTINGS_CARD_ANCHORS.members,
    title: msg`Members`,
    description: msg`Who can sign in, and who can change settings.`,
    keywords: ["members", "people", "invite", "roles", "owner", "admin"],
  },
  {
    group: "advanced",
    anchor: SETTINGS_CARD_ANCHORS.speakerRecognition,
    title: msg`Speaker turns and voice matching`,
    description: msg`How voices are told apart and matched to people you already know.`,
    keywords: [
      "auto-identify",
      "known voices",
      "voiceprint",
      "echo suppression",
      "two-channel",
      "match strictness",
      "confidence threshold",
      "diarization",
      "speaker turns",
      "deepgram",
    ],
  },
  {
    group: "advanced",
    anchor: SETTINGS_CARD_ANCHORS.finishedTranscript,
    title: msg`Finished transcript`,
    description: msg`How a finished recording becomes the transcript you keep.`,
    keywords: [
      "ensemble",
      "two-pass",
      "merge",
      "constrained",
      "rewrite",
      "word engines",
      "speaker turns",
      "soniox",
      "assemblyai",
      "azure-llm",
      "headcount",
    ],
  },
  {
    group: "integrations",
    anchor: SETTINGS_CARD_ANCHORS.connectClaude,
    title: msg`Connect Claude`,
    description: msg`Let Claude read your meetings and ask questions about them.`,
    keywords: ["claude", "mcp", "connector", "claude.ai", "claude desktop", "claude code", "anthropic"],
  },
  {
    group: "advanced",
    anchor: SETTINGS_CARD_ANCHORS.llmProviders,
    title: msg`AI models`,
    description: msg`The accounts that write summaries and answers.`,
    keywords: ["anthropic", "claude", "openai", "openrouter", "api key", "llm"],
  },
  {
    group: "advanced",
    anchor: SETTINGS_CARD_ANCHORS.models,
    title: msg`Model selection`,
    description: msg`Which model does each kind of work. Cheaper models use fewer credits.`,
    keywords: [
      "light model",
      "default model",
      "heavy model",
      "tier",
      "per-task",
      "override",
      "credits",
      "capability",
      "live notes",
      "speaker matching",
      "meeting summary",
      "report writing",
    ],
  },
  {
    group: "advanced",
    anchor: SETTINGS_CARD_ANCHORS.prompt,
    title: msg`Analysis prompt`,
    description: msg`The instructions a meeting summary is written from.`,
    keywords: ["system prompt", "instructions", "summary prompt"],
  },
  {
    group: "advanced",
    anchor: SETTINGS_CARD_ANCHORS.image,
    title: msg`Image generation`,
    description: msg`Where the pictures in reports come from.`,
    keywords: ["runware", "image model", "image provider", "api key", "pictures"],
  },
  {
    group: "reports",
    anchor: SETTINGS_CARD_ANCHORS.reportTypes,
    title: msg`Report types`,
    description: msg`Which report styles this workspace can generate, and when the heavy model is allowed.`,
    keywords: ["visual reports", "best model", "heavy model for reports", "fit prompt", "threshold", "report matching"],
  },
  {
    group: "reports",
    anchor: SETTINGS_CARD_ANCHORS.summaryLayout,
    title: msg`Meeting layout`,
    description: msg`Arrange the blocks that make up a meeting summary.`,
    keywords: ["summary blocks", "layout", "template", "curate"],
  },
  {
    group: "advanced",
    anchor: SETTINGS_CARD_ANCHORS.budget,
    title: msg`Monthly limits`,
    description: msg`A monthly credit limit for each service.`,
    keywords: ["budget", "credit limit", "limit", "per-provider budget", "hourly rate", "monthly limit"],
  },
  {
    group: "look",
    anchor: SETTINGS_CARD_ANCHORS.appearance,
    title: msg`Logo and colour`,
    description: msg`Your logo, brand colour, and the default theme.`,
    keywords: ["theme", "branding", "logo", "brand colour", "sign-in", "colours", "colors", "look"],
  },
  {
    group: "integrations",
    anchor: SETTINGS_CARD_ANCHORS.integrations,
    title: msg`Microsoft Teams`,
    description: msg`Connect Microsoft Teams so meetings can show up here.`,
    keywords: ["microsoft teams", "webhook", "tenant id", "connectors"],
  },
]
